import { ReportingBucket } from '../types/buckets';
import type {
  BreakdownListId,
  IComponentFacts,
  ReportComponentId,
} from '../types/report';
import type { ITableModel } from '../types/tables';
import { BUCKET_LABELS, OPEX_BUCKETS } from './BucketHelper';
import { ReportFormatHelper } from './ReportFormatHelper';

export interface IBreakdownRow {
  key: string;
  name: string;
  amount: number;
  /** Share of the list total, as a ratio. */
  share: number | null;
  /** Change versus the comparison window; null without a comparison. */
  delta: number | null;
  /** Amount relative to the largest row, for the bar under the row. */
  scale: number;
  /** Rows folded into this one, when it is the "Other" row. */
  foldedCount?: number;
}

export interface IBreakdownListModel {
  /** e.g. "Operating expenses, Aug 2026". */
  heading: string;
  total: string;
  /** Top rows plus, when needed, one folded "Other" row. */
  rows: IBreakdownRow[];
  /** Every underlying row, ranked, for exports. */
  allRows: IBreakdownRow[];
  hasComparison: boolean;
}

interface IBreakdownSpec {
  heading: string;
  rows: (facts: IComponentFacts) => BreakdownInput[];
}

const TOP_ROWS = 5;

type BreakdownInput = Omit<IBreakdownRow, 'share' | 'scale'>;

const LIST_BY_COMPONENT: Partial<Record<ReportComponentId, BreakdownListId>> = {
  opex_breakdown_list: 'opex',
  revenue_breakdown_list: 'revenue',
};

/** Ranked bucket / ledger-line breakdowns built from existing period totals. */
export class BreakdownListHelper {
  private static readonly SPECS: Record<BreakdownListId, IBreakdownSpec> = {
    opex: {
      heading: 'Operating expenses',
      rows: ({ current, comparison }) =>
        OPEX_BUCKETS.map((bucket) => ({
          key: bucket,
          name: BUCKET_LABELS[bucket],
          amount: current.buckets[bucket],
          delta: comparison
            ? current.buckets[bucket] - comparison.buckets[bucket]
            : null,
        })),
    },
    revenue: {
      heading: 'Revenue',
      rows: ({ table, current, comparison }) =>
        table.lines
          .filter(
            (line) =>
              line.section === 'revenue' &&
              table.bucketMap.get(line.id) === ReportingBucket.REVENUE
          )
          .map((line) => ({
            key: line.id,
            name: line.name,
            amount: current.lines[line.id] ?? 0,
            delta: comparison
              ? (current.lines[line.id] ?? 0) - (comparison.lines[line.id] ?? 0)
              : null,
          })),
    },
  };

  static listIdFor(componentId: ReportComponentId): BreakdownListId | null {
    return LIST_BY_COMPONENT[componentId] ?? null;
  }

  static model(
    id: BreakdownListId,
    facts: IComponentFacts
  ): IBreakdownListModel {
    return BreakdownListHelper.fromRows(
      id,
      BreakdownListHelper.SPECS[id].rows(facts),
      facts
    );
  }

  private static fromRows(
    id: BreakdownListId,
    input: BreakdownInput[],
    facts: IComponentFacts
  ): IBreakdownListModel {
    const spec = BreakdownListHelper.SPECS[id];
    const raw = input
      .filter((r) => r.amount !== 0 || r.delta)
      .sort((a, b) => b.amount - a.amount);
    const total = raw.reduce((s, r) => s + r.amount, 0);
    const largest = raw[0]?.amount ?? 0;
    const allRows = raw.map((r) => BreakdownListHelper.rank(r, total, largest));

    const rows =
      allRows.length > TOP_ROWS + 1
        ? [
            ...allRows.slice(0, TOP_ROWS),
            BreakdownListHelper.fold(allRows.slice(TOP_ROWS), total, largest),
          ]
        : allRows;

    return {
      heading: `${spec.heading}, ${facts.resolved.label}`,
      total: ReportFormatHelper.currency(total, facts.currency, {
        compact: true,
      }),
      rows,
      allRows,
      hasComparison: facts.comparison !== null,
    };
  }

  static rowText(row: IBreakdownRow, currency: string): string {
    const parts = [
      ReportFormatHelper.currency(row.amount, currency, { compact: true }),
      ReportFormatHelper.percent(row.share, 0),
    ];
    if (row.delta !== null)
      parts.push(ReportFormatHelper.delta(row.delta, currency));
    return parts.join(' · ');
  }

  /** Every row, unfolded, for the XLSX export. */
  static toTableModel(
    model: IBreakdownListModel,
    comparisonLabel: string
  ): ITableModel {
    return {
      columns: [
        { key: 'label', label: '', format: 'text' },
        { key: 'amount', label: 'Amount', format: 'currency' },
        { key: 'share', label: 'Share', format: 'percent' },
        { key: 'delta', label: `Δ ${comparisonLabel}`, format: 'delta' },
      ],
      rows: model.allRows.map((row) => ({
        key: row.key,
        label: row.name,
        cells: [row.amount, row.share, row.delta],
        kind: 'line',
      })),
    };
  }

  private static rank(
    row: Omit<IBreakdownRow, 'share' | 'scale'>,
    total: number,
    largest: number
  ): IBreakdownRow {
    return {
      ...row,
      share: total ? row.amount / total : null,
      scale: largest > 0 ? Math.max(0, row.amount) / largest : 0,
    };
  }

  private static fold(
    rest: IBreakdownRow[],
    total: number,
    largest: number
  ): IBreakdownRow {
    const amount = rest.reduce((s, r) => s + r.amount, 0);
    const deltas = rest.map((r) => r.delta);
    return BreakdownListHelper.rank(
      {
        key: 'other',
        name: `Other (${rest.length} categories)`,
        amount,
        delta: deltas.every((d): d is number => d !== null)
          ? deltas.reduce((s, d) => s + d, 0)
          : null,
        foldedCount: rest.length,
      },
      total,
      largest
    );
  }
}
